/**
 * FilterModal Component (Redesigned)
 * Advanced filtering for workbench playlists with studio monitor aesthetic
 *
 * Features:
 * - Tab-based action selection with color-coded modes
 * - Real-time preview with 300ms debouncing
 * - Fixed status bar with progress visualization
 * - Preview flow (local preview → commit to backend)
 * - Multi-select genre/artist tags
 * - Dual-thumb range sliders with heatmaps
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Filter, Sparkles, Plus, Trash2, Check, Loader2, Play } from 'lucide-react';
import { Range, getTrackBackground } from 'react-range';
import { useWorkbenchStore } from '../../stores/workbench';
import { workbenchService, type FilterCriteria, type FilterAction } from '../../api/services/workbench';
import { genreService } from '../../api/services/genres';
import type { TrackWithInstanceId } from '../../api/types';
import { audioFeaturesService } from '../../api/services/tracks';
import type { BatchGenreResponse, AudioFeatures } from '../../api/types';

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  audioFeaturesMap?: Map<string, AudioFeatures>;
  trackIds?: string[];
}

/**
 * Color themes for each action mode
 * Provides consistent visual identity across tabs, descriptions, and buttons
 */
const MODE_THEMES = {
  create_playlist: {
    primary: '#10b981', // emerald-500
    light: '#34d399', // emerald-400
    dark: '#059669', // emerald-600
    bg: 'rgba(16, 185, 129, 0.1)',
    border: 'rgba(16, 185, 129, 0.3)',
    text: 'text-emerald-400',
    bgClass: 'bg-emerald-500',
    borderClass: 'border-emerald-500',
    hoverClass: 'hover:bg-emerald-600',
  },
  remove_filtered: {
    primary: '#f43f5e', // rose-500
    light: '#fb7185', // rose-400
    dark: '#e11d48', // rose-600
    bg: 'rgba(244, 63, 94, 0.1)',
    border: 'rgba(244, 63, 94, 0.3)',
    text: 'text-rose-400',
    bgClass: 'bg-rose-500',
    borderClass: 'border-rose-500',
    hoverClass: 'hover:bg-rose-600',
  },
  keep_only_filtered: {
    primary: '#3b82f6', // blue-500
    light: '#60a5fa', // blue-400
    dark: '#2563eb', // blue-600
    bg: 'rgba(59, 130, 246, 0.1)',
    border: 'rgba(59, 130, 246, 0.3)',
    text: 'text-blue-400',
    bgClass: 'bg-blue-500',
    borderClass: 'border-blue-500',
    hoverClass: 'hover:bg-blue-600',
  },
} as const;

/**
 * Calculate distribution of values into bins for heatmap visualization
 */
function calculateDistribution(
  values: number[],
  min: number,
  max: number,
  numBins: number
): number[] {
  const bins = Array(numBins).fill(0);
  const binSize = (max - min) / numBins;

  values.forEach((val) => {
    const binIndex = Math.min(Math.floor((val - min) / binSize), numBins - 1);
    bins[binIndex]++;
  });

  return bins;
}

/**
 * HeatmapBar - Visual distribution of audio feature values
 */
interface HeatmapBarProps {
  values: number[];
  min: number;
  max: number;
  color: string;
}

function HeatmapBar({ values, min, max, color }: HeatmapBarProps) {
  const bins = calculateDistribution(values, min, max, 24);
  const maxCount = Math.max(...bins, 1);

  return (
    <div className="h-2 w-full rounded-full overflow-hidden flex gap-px bg-zinc-900">
      {bins.map((count, idx) => (
        <div
          key={idx}
          className="flex-1 transition-all duration-300"
          style={{
            backgroundColor: count > 0 ? color : 'transparent',
            opacity: count > 0 ? 0.3 + (count / maxCount) * 0.7 : 0,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Dual-thumb range slider with color theming
 */
function RangeSlider({
  label,
  min,
  max,
  step,
  value,
  onChange,
  formatValue,
  heatmapValues,
  color,
}: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  formatValue?: (val: number) => string;
  heatmapValues?: number[];
  color: string;
}) {
  const format = formatValue || ((v: number) => v.toString());
  const values = value;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-zinc-300">{label}</label>
        <span className="text-xs font-mono text-zinc-400 tracking-wider">
          {format(values[0])} → {format(values[1])}
        </span>
      </div>

      {/* Heatmap visualization */}
      {heatmapValues && heatmapValues.length > 0 && (
        <HeatmapBar values={heatmapValues} min={min} max={max} color={color} />
      )}

      <Range
        values={values}
        step={step}
        min={min}
        max={max}
        onChange={(newValues) => {
          if (newValues[0] !== undefined && newValues[1] !== undefined) {
            onChange([newValues[0], newValues[1]]);
          }
        }}
        renderTrack={({ props, children }) => (
          <div
            onMouseDown={props.onMouseDown}
            onTouchStart={props.onTouchStart}
            style={{
              ...props.style,
              height: '36px',
              display: 'flex',
              width: '100%',
            }}
          >
            <div
              ref={props.ref}
              style={{
                height: '6px',
                width: '100%',
                borderRadius: '4px',
                background: getTrackBackground({
                  values,
                  colors: ['#18181b', color, '#18181b'],
                  min,
                  max,
                }),
                alignSelf: 'center',
              }}
            >
              {children}
            </div>
          </div>
        )}
        renderThumb={({ props, isDragged }) => (
          <div
            {...props}
            key={props.key}
            style={{
              ...props.style,
              height: '20px',
              width: '20px',
              borderRadius: '50%',
              backgroundColor: color,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: isDragged
                ? `0 0 0 4px ${color}40`
                : '0 2px 6px rgba(0, 0, 0, 0.5)',
              outline: 'none',
              transition: 'box-shadow 150ms',
            }}
          >
            <div
              style={{
                height: '10px',
                width: '10px',
                borderRadius: '50%',
                backgroundColor: isDragged ? '#ffffff' : '#ffffff90',
              }}
            />
          </div>
        )}
      />
    </div>
  );
}

export function FilterModal({
  isOpen,
  onClose,
  onSuccess,
  audioFeaturesMap: propsAudioFeaturesMap,
  trackIds: propsTrackIds,
}: FilterModalProps) {
  const activePlaylist = useWorkbenchStore((state) => state.activePlaylist);
  const tracks = useWorkbenchStore((state) => state.tracks);
  const setFilterPreview = useWorkbenchStore((state) => state.setFilterPreview);

  // Filter criteria state
  const [criteria, setCriteria] = useState<FilterCriteria & { minGenreWeight: number }>({
    bpm: { min: 60, max: 200 },
    energy: { min: 0.0, max: 1.0 },
    valence: { min: 0.0, max: 1.0 },
    danceability: { min: 0.0, max: 1.0 },
    genres: [],
    minGenreWeight: 0.3,
    artists: [],
    artistMode: 'exclude',
  });

  // Action state - NOW AT TOP OF UI
  const [action, setAction] = useState<FilterAction>('create_playlist');
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [isApplying, setIsApplying] = useState(false);

  // Preview state
  const [matchedCount, setMatchedCount] = useState<number | null>(null);
  const [matchedTrackIds, setMatchedTrackIds] = useState<string[]>([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Available genres/artists
  const [availableGenres, setAvailableGenres] = useState<string[]>([]);
  const [isLoadingGenres, setIsLoadingGenres] = useState(false);
  const [availableArtists, setAvailableArtists] = useState<string[]>([]);

  // Audio features
  const [fetchedAudioFeaturesMap, setFetchedAudioFeaturesMap] = useState<Map<string, AudioFeatures>>(
    new Map()
  );
  const audioFeaturesMap = propsAudioFeaturesMap || fetchedAudioFeaturesMap;

  // Get current theme based on action
  const theme = MODE_THEMES[action];

  // Calculate match percentage for progress bar
  const matchPercentage = useMemo(() => {
    if (matchedCount === null || tracks.length === 0) return 0;
    return (matchedCount / tracks.length) * 100;
  }, [matchedCount, tracks.length]);

  /**
   * Fetch available genres on mount
   */
  useEffect(() => {
    async function fetchGenres() {
      if (!activePlaylist || tracks.length === 0) return;

      setIsLoadingGenres(true);
      try {
        const trackIds = tracks.map((t) => t.id);
        const response: BatchGenreResponse = await genreService.getTrackGenresBatch(trackIds);

        const genreSet = new Set<string>();
        response.tracks.forEach((trackGenres) => {
          trackGenres.genres.forEach((genre) => {
            if (genre.weight >= criteria.minGenreWeight) {
              genreSet.add(genre.name);
            }
          });
        });

        setAvailableGenres(Array.from(genreSet).sort());
      } catch (error) {
        console.error('Failed to fetch genres:', error);
      } finally {
        setIsLoadingGenres(false);
      }
    }

    if (isOpen) {
      fetchGenres();
    }
  }, [isOpen, activePlaylist, tracks, criteria.minGenreWeight]);

  /**
   * Fetch audio features for heatmap visualization
   */
  useEffect(() => {
    async function fetchAudioFeatures() {
      if (propsAudioFeaturesMap || !isOpen || tracks.length === 0) return;

      try {
        const trackIds = tracks.map((t) => t.id);
        const response = await audioFeaturesService.getBatch(trackIds);

        const featureMap = new Map<string, AudioFeatures>();
        response.audioFeatures.forEach((f) => featureMap.set(f.id, f));
        setFetchedAudioFeaturesMap(featureMap);
      } catch (error) {
        console.error('Failed to fetch audio features:', error);
      }
    }

    if (isOpen && !propsAudioFeaturesMap) {
      fetchAudioFeatures();
    }
  }, [isOpen, tracks, propsAudioFeaturesMap]);

  /**
   * Extract available artists from tracks
   */
  useEffect(() => {
    if (!isOpen || tracks.length === 0) return;

    const artistSet = new Set<string>();
    tracks.forEach((track) => {
      track.artists?.forEach((artist) => {
        artistSet.add(artist.name);
      });
    });

    setAvailableArtists(Array.from(artistSet).sort());
  }, [isOpen, tracks]);

  /**
   * Reset artistMode to 'include' when switching to remove/keep actions
   * This prevents confusion from double negation logic
   */
  useEffect(() => {
    if (action !== 'create_playlist' && criteria.artistMode === 'exclude') {
      setCriteria((prev) => ({ ...prev, artistMode: 'include' }));
    }
  }, [action]);

  /**
   * Fetch preview when criteria changes (debounced)
   */
  useEffect(() => {
    async function fetchPreview() {
      if (!activePlaylist) return;

      setIsLoadingPreview(true);
      try {
        const trackIdsToSend = propsTrackIds || tracks.map((t) => t.id);

        // For remove/keep actions, force artistMode to 'include' to avoid double negation
        const previewCriteria = {
          ...criteria,
          artistMode: (action === 'create_playlist' ? criteria.artistMode : 'include') as 'include' | 'exclude',
        };

        const response = await workbenchService.previewFilter(
          activePlaylist.id,
          trackIdsToSend,
          previewCriteria
        );
        setMatchedCount(response.matchedCount);
        setMatchedTrackIds(response.matchedTrackIds);
      } catch (error) {
        console.error('Failed to fetch preview:', error);
        setMatchedCount(null);
        setMatchedTrackIds([]);
      } finally {
        setIsLoadingPreview(false);
      }
    }

    const timer = setTimeout(fetchPreview, 300);
    return () => clearTimeout(timer);
  }, [criteria, activePlaylist, propsTrackIds, tracks, action]);

  /**
   * Extract feature values for heatmap
   */
  const extractFeatureValues = useCallback(
    (feature: 'energy' | 'valence' | 'danceability' | 'bpm'): number[] => {
      return tracks
        .map((t) => {
          const features = audioFeaturesMap.get(t.id);
          if (!features) return null;

          switch (feature) {
            case 'energy':
              return features.energy;
            case 'valence':
              return features.valence;
            case 'danceability':
              return features.danceability;
            case 'bpm':
              return features.tempo;
          }
        })
        .filter((v): v is number => v !== null);
    },
    [tracks, audioFeaturesMap]
  );

  /**
   * Toggle genre selection
   */
  const toggleGenre = useCallback((genre: string) => {
    setCriteria((prev) => {
      const current = prev.genres || [];
      const updated = current.includes(genre)
        ? current.filter((g) => g !== genre)
        : [...current, genre];
      return { ...prev, genres: updated };
    });
  }, []);

  /**
   * Toggle artist selection
   */
  const toggleArtist = useCallback((artist: string) => {
    setCriteria((prev) => {
      const current = prev.artists || [];
      const updated = current.includes(artist)
        ? current.filter((a) => a !== artist)
        : [...current, artist];
      return { ...prev, artists: updated };
    });
  }, []);

  /**
   * Handle preview - applies filter locally, shows in workbench
   * User can then commit or discard from WorkbenchActionBar
   */
  const handlePreview = () => {
    if (!activePlaylist || matchedCount === null || matchedTrackIds.length === 0) return;

    // Calculate preview tracks based on action
    let previewTracks: TrackWithInstanceId[];
    if (action === 'remove_filtered') {
      previewTracks = tracks.filter((t) => !matchedTrackIds.includes(t.id));
    } else if (action === 'keep_only_filtered') {
      previewTracks = tracks.filter((t) => matchedTrackIds.includes(t.id));
    } else {
      // For create_playlist, no preview needed
      return;
    }

    // For remove/keep actions, force artistMode to 'include' to avoid double negation
    const finalCriteria = {
      ...criteria,
      artistMode: 'include' as const,
    };

    // Set preview mode with metadata
    setFilterPreview(
      {
        action,
        criteria: finalCriteria,
        newPlaylistName,
        matchedTrackIds,
        matchedCount,
      },
      previewTracks
    );

    onClose();
  };

  /**
   * Handle direct apply (for create_playlist action)
   */
  const handleApply = async () => {
    if (!activePlaylist) return;

    setIsApplying(true);
    try {
      // For remove/keep actions, force artistMode to 'include' to avoid double negation
      const finalCriteria = {
        ...criteria,
        artistMode: (action === 'create_playlist' ? criteria.artistMode : 'include') as 'include' | 'exclude',
      };

      await workbenchService.applyFilter(activePlaylist.id, {
        ...finalCriteria,
        action,
        newPlaylistName: action === 'create_playlist' ? newPlaylistName : undefined,
      });

      // For create_playlist, no local state change needed
      // For remove/keep, we handle via preview flow

      onClose();
      onSuccess?.();
    } catch (error) {
      console.error('Failed to apply filter:', error);
    } finally {
      setIsApplying(false);
    }
  };

  /**
   * Get dynamic button text based on action and counts
   */
  const getButtonText = () => {
    if (isApplying) return 'Applying...';
    if (matchedCount === null) return 'Loading...';

    const count = matchedCount;
    const remaining = tracks.length - matchedCount;

    switch (action) {
      case 'create_playlist':
        return `Create Playlist (${count} tracks)`;
      case 'remove_filtered':
        return `Preview Removal (${count} tracks)`;
      case 'keep_only_filtered':
        return `Preview Keep (${count} tracks, remove ${remaining})`;
    }
  };

  /**
   * Get contextual description for current mode
   */
  const getDescription = () => {
    if (matchedCount === null) return 'Calculating matches...';

    const count = matchedCount;
    const remaining = tracks.length - matchedCount;

    switch (action) {
      case 'create_playlist':
        return `Create a new playlist with ${count} matching tracks. Your current playlist will not be modified.`;
      case 'remove_filtered':
        return `Remove ${count} matching tracks from the current playlist. ${remaining} tracks will remain.`;
      case 'keep_only_filtered':
        return `Keep ${count} matching tracks and remove ${remaining} non-matching tracks from the current playlist.`;
    }
  };

  if (!isOpen || !activePlaylist) return null;

  const canApply =
    !isApplying &&
    matchedCount !== null &&
    matchedCount > 0 &&
    (action !== 'create_playlist' || newPlaylistName.trim());

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-[101] p-4 pointer-events-none">
        <div
          className="bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col pointer-events-auto animate-in zoom-in-95 fade-in duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with Close Button */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-gradient-to-br from-zinc-800 to-zinc-900 border border-zinc-700">
                <Filter className="w-5 h-5 text-zinc-300" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-zinc-100 tracking-tight">
                  Advanced Filter
                </h2>
                <p className="text-xs text-zinc-500 font-medium">
                  Audio Features · Genres · Artists
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-200 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Action Tabs */}
          <div className="px-6 pb-4">
            <div className="flex gap-2 p-1 bg-zinc-950 rounded-lg border border-zinc-800">
              {/* Create Playlist Tab */}
              <button
                onClick={() => setAction('create_playlist')}
                className={`
                  flex-1 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200
                  ${
                    action === 'create_playlist'
                      ? 'bg-emerald-500 text-white shadow-lg'
                      : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900'
                  }
                `}
              >
                <Plus className="inline w-4 h-4 mr-1.5 -mt-0.5" />
                Create Playlist
              </button>

              {/* Remove Matches Tab */}
              <button
                onClick={() => setAction('remove_filtered')}
                className={`
                  flex-1 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200
                  ${
                    action === 'remove_filtered'
                      ? 'bg-rose-500 text-white shadow-lg'
                      : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900'
                  }
                `}
              >
                <Trash2 className="inline w-4 h-4 mr-1.5 -mt-0.5" />
                Remove Matches
              </button>

              {/* Keep Only Tab */}
              <button
                onClick={() => setAction('keep_only_filtered')}
                className={`
                  flex-1 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200
                  ${
                    action === 'keep_only_filtered'
                      ? 'bg-blue-500 text-white shadow-lg'
                      : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900'
                  }
                `}
              >
                <Sparkles className="inline w-4 h-4 mr-1.5 -mt-0.5" />
                Keep Only Matches
              </button>
            </div>
          </div>

          {/* Contextual Description */}
          <div
            className="mx-6 mb-4 px-4 py-3 rounded-lg border transition-all duration-300"
            style={{
              backgroundColor: theme.bg,
              borderColor: theme.border,
            }}
          >
            <p className={`text-sm ${theme.text} font-medium leading-relaxed`}>
              {getDescription()}
            </p>

            {/* Playlist Name Input (only for create action) */}
            {action === 'create_playlist' && (
              <input
                type="text"
                placeholder="Enter playlist name..."
                value={newPlaylistName}
                onChange={(e) => setNewPlaylistName(e.target.value)}
                className="mt-3 w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
              />
            )}
          </div>

          {/* Scrollable Filter Criteria */}
          <div className="flex-1 overflow-y-auto px-6 space-y-6 pb-6">
            {/* Audio Feature Sliders */}
            <div className="space-y-4">
              <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">
                Audio Features
              </h3>

              <RangeSlider
                label="BPM (Tempo)"
                min={60}
                max={200}
                step={1}
                value={[criteria.bpm?.min ?? 60, criteria.bpm?.max ?? 200]}
                onChange={([min, max]) => setCriteria({ ...criteria, bpm: { min, max } })}
                formatValue={(v) => `${v}`}
                heatmapValues={extractFeatureValues('bpm')}
                color={theme.primary}
              />

              <RangeSlider
                label="Energy"
                min={0}
                max={1}
                step={0.05}
                value={[criteria.energy?.min ?? 0, criteria.energy?.max ?? 1]}
                onChange={([min, max]) => setCriteria({ ...criteria, energy: { min, max } })}
                formatValue={(v) => `${Math.round(v * 100)}%`}
                heatmapValues={extractFeatureValues('energy')}
                color={theme.primary}
              />

              <RangeSlider
                label="Valence (Positivity)"
                min={0}
                max={1}
                step={0.05}
                value={[criteria.valence?.min ?? 0, criteria.valence?.max ?? 1]}
                onChange={([min, max]) => setCriteria({ ...criteria, valence: { min, max } })}
                formatValue={(v) => `${Math.round(v * 100)}%`}
                heatmapValues={extractFeatureValues('valence')}
                color={theme.primary}
              />

              <RangeSlider
                label="Danceability"
                min={0}
                max={1}
                step={0.05}
                value={[
                  criteria.danceability?.min ?? 0,
                  criteria.danceability?.max ?? 1,
                ]}
                onChange={([min, max]) =>
                  setCriteria({ ...criteria, danceability: { min, max } })
                }
                formatValue={(v) => `${Math.round(v * 100)}%`}
                heatmapValues={extractFeatureValues('danceability')}
                color={theme.primary}
              />
            </div>

            {/* Genre Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">
                  Genres
                </h3>
                {isLoadingGenres && (
                  <span className="text-xs text-zinc-500">Loading...</span>
                )}
              </div>

              {availableGenres.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {availableGenres.map((genre) => {
                    const isSelected = (criteria.genres || []).includes(genre);
                    return (
                      <button
                        key={genre}
                        onClick={() => toggleGenre(genre)}
                        className={`
                          px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                          ${
                            isSelected
                              ? `${theme.bgClass} text-white border ${theme.borderClass}`
                              : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600'
                          }
                        `}
                      >
                        {isSelected && <Check className="inline w-3 h-3 mr-1" />}
                        {genre}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-zinc-600">No genres available</p>
              )}
            </div>

            {/* Artist Filtering - Context-aware based on action */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">
                  {action === 'create_playlist'
                    ? 'Artists'
                    : action === 'remove_filtered'
                    ? 'Artists to Remove'
                    : 'Artists to Keep'}
                </h3>

                {/* Exclude/Include Toggle - Only show for Create Playlist */}
                {action === 'create_playlist' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCriteria({ ...criteria, artistMode: 'exclude' })}
                      className={`px-3 py-1 rounded text-xs font-medium transition ${
                        criteria.artistMode === 'exclude'
                          ? 'bg-red-500 text-white'
                          : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300'
                      }`}
                    >
                      Exclude
                    </button>
                    <button
                      onClick={() => setCriteria({ ...criteria, artistMode: 'include' })}
                      className={`px-3 py-1 rounded text-xs font-medium transition ${
                        criteria.artistMode === 'include'
                          ? 'bg-blue-500 text-white'
                          : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300'
                      }`}
                    >
                      Include
                    </button>
                  </div>
                )}
              </div>

              {availableArtists.length > 0 ? (
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                  {availableArtists.map((artist) => {
                    const isSelected = (criteria.artists || []).includes(artist);

                    // Color logic based on action
                    let buttonColor = 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600';
                    if (isSelected) {
                      if (action === 'create_playlist') {
                        // Use artistMode for create_playlist
                        buttonColor = criteria.artistMode === 'exclude'
                          ? 'bg-red-500 text-white border border-red-400'
                          : 'bg-blue-500 text-white border border-blue-400';
                      } else if (action === 'remove_filtered') {
                        // Remove mode = red (destructive)
                        buttonColor = 'bg-rose-500 text-white border border-rose-400';
                      } else {
                        // Keep mode = blue (selective)
                        buttonColor = 'bg-blue-500 text-white border border-blue-400';
                      }
                    }

                    return (
                      <button
                        key={artist}
                        onClick={() => toggleArtist(artist)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${buttonColor}`}
                      >
                        {isSelected && <Check className="inline w-3 h-3 mr-1" />}
                        {artist}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-zinc-600">No artists available</p>
              )}

              <p className="text-xs text-zinc-600">
                {action === 'create_playlist'
                  ? criteria.artistMode === 'exclude'
                    ? 'Selected artists will be excluded from the new playlist'
                    : 'Only tracks by selected artists will be included in the new playlist'
                  : action === 'remove_filtered'
                  ? 'Selected artists will be removed from the playlist'
                  : 'Only tracks by selected artists will be kept in the playlist'}
              </p>
            </div>
          </div>

          {/* Fixed Status Bar at Bottom */}
          <div className="border-t border-zinc-800 bg-zinc-950">
            {/* Progress Bar */}
            <div className="h-1 bg-zinc-900 overflow-hidden">
              <div
                className="h-full transition-all duration-500 ease-out"
                style={{
                  width: `${matchPercentage}%`,
                  backgroundColor: theme.primary,
                }}
              />
            </div>

            {/* Status Info & Actions */}
            <div className="px-6 py-4 flex items-center justify-between">
              {/* Left: Match Count */}
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-xs text-zinc-500 font-medium uppercase tracking-wider">
                    Matching Tracks
                  </div>
                  <div className="text-lg font-mono font-bold text-zinc-200 tracking-tight">
                    {isLoadingPreview ? (
                      <span className="text-zinc-500">Calculating...</span>
                    ) : matchedCount !== null ? (
                      <>
                        <span style={{ color: theme.primary }}>{matchedCount}</span>
                        <span className="text-zinc-600"> / </span>
                        <span>{tracks.length}</span>
                      </>
                    ) : (
                      <span className="text-zinc-600">—</span>
                    )}
                  </div>
                </div>

                {matchedCount !== null && (
                  <div className="text-sm text-zinc-500 font-medium">
                    {matchPercentage.toFixed(1)}%
                  </div>
                )}
              </div>

              {/* Right: Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-5 py-2.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={action === 'create_playlist' ? handleApply : handlePreview}
                  disabled={!canApply}
                  className={`
                    px-5 py-2.5 rounded-lg text-white transition-all text-sm font-medium
                    flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed
                    ${theme.bgClass} ${theme.hoverClass}
                  `}
                  style={{
                    boxShadow: canApply ? `0 0 20px ${theme.primary}40` : 'none',
                  }}
                >
                  {isApplying ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : action === 'create_playlist' ? (
                    <Plus className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  {getButtonText()}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
